import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTitle,
  TypeList,
  TypeListItem,
  cssSpacing,
} from '@spotify-internal/encore-web';
import React, { useState } from 'react';
import styled from 'styled-components';

import { ChildCategory } from 'lib/sanity/types';
import { ArticleLink } from 'components/Links';
import { useUbiCategoryNavigation } from 'utils/ubi/pages/useUbiCategoryNavigation';

const StyledAccordion = styled(Accordion)`
  background-color: transparent;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-block-end: 0;
  padding-inline-start: ${cssSpacing('tighter-2')};
`;

const StyledAccordionItem = styled(AccordionItem)`
  border: 0;
  &:last-child {
    border-block-end: 0;
  }
`;

const StyledAccordionTitle = styled(AccordionTitle)`
  padding: 0;

  & span {
    font-size: 16px;
  }

  & svg {
    block-size: 16px;
    inline-size: 16px;
  }
`;

const StyledAccordionContent = styled(AccordionContent)`
  && {
    padding: 24px 16px 0;
  }
`;

const StyledTypeList = styled(TypeList)`
  display: flex;
  flex-direction: column;
  gap: ${cssSpacing('base')};
`;

const StyledTextLink = styled(ArticleLink)`
  text-decoration: none !important;
`;

export const ArticleChildCategories = ({
  categories,
  parentSlug,
  initialIndex,
}: {
  categories: ChildCategory[];
  parentSlug: string;
  initialIndex: number;
}) => {
  const [activeIndex, updateActive] = useState(initialIndex);
  const ubi = useUbiCategoryNavigation();

  return (
    <StyledAccordion aria-label="Accordion group for child categories">
      {categories
        .filter(({ title }) => title !== null)
        .map(({ slug, title, articles }, index) => (
          <StyledAccordionItem key={slug + title}>
            <StyledAccordionTitle
              heading="h3"
              onClick={e => {
                e.preventDefault();
                ubi.logChildCategoryTitleInteraction({
                  expanded: index !== activeIndex,
                  parentCategory: parentSlug,
                  container: `child-category-${slug}-container`,
                  identifier: slug,
                });
                updateActive(index === activeIndex ? -1 : index);
              }}
              isExpanded={index === activeIndex}
            >
              {title}
            </StyledAccordionTitle>
            <StyledAccordionContent isExpanded={index === activeIndex}>
              <StyledTypeList condensedAll listStyleReset>
                {articles
                  .filter(
                    ({ slug: articleSlug, title: articleTitle }) =>
                      articleTitle !== null && articleSlug.toLowerCase() !== 'contact-us',
                  )
                  .map(({ slug: articleSlug, title: articleTitle }) => (
                    <TypeListItem
                      onClick={() => {
                        ubi.logArticleLinkInteraction({
                          parentCategory: parentSlug,
                          childCategory: slug,
                          childCategoryContainer: `child-category-${slug}-container`,
                          container: `article-${articleSlug}-container`,
                          identifier: articleSlug,
                          uri: articleSlug,
                        });
                      }}
                      key={articleSlug + articleTitle}
                      variant="bodyMedium"
                      condensed
                      listStyleReset
                    >
                      <StyledTextLink slug={articleSlug}>{articleTitle}</StyledTextLink>
                    </TypeListItem>
                  ))}
              </StyledTypeList>
            </StyledAccordionContent>
          </StyledAccordionItem>
        ))}
    </StyledAccordion>
  );
};
